import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { ArchiveBoxXMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { IPart } from "../../models/Part";

interface ScanPartActionModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  part: IPart | null;
  onRemove: () => void;
  onDamage: () => void;
}

// Shown when a part is scanned on the job. The user can pick an action, or
// scan the same part again to confirm removal (handled by the caller).
export default function ScanPartActionModal({
  open,
  setOpen,
  part,
  onRemove,
  onDamage,
}: ScanPartActionModalProps) {
  const { t } = useTranslation("jobs");
  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-lg"}>
      <div className="text-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {part?.title ?? t("Scanned Part")}
        </h3>
        {part?.tagNumber && (
          <p className="text-sm text-gray-500 mt-1">{t("Tag: {{tag}}", { tag: part.tagNumber })}</p>
        )}
        {part?.id && (
          <div className="mt-4 flex flex-col items-center gap-1">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(part.id)}`}
              alt={t("Part QR code")}
              className="h-40 w-40 rounded-md bg-white"
            />
            <p className="text-xs text-gray-400">{t("Scan to confirm removal")}</p>
          </div>
        )}
        <p className="text-sm text-gray-600 mt-4">
          {t("Scan this part again to confirm removal, or choose an action below.")}
        </p>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          className="inline-flex flex-1 items-center justify-center gap-x-2 rounded-md border border-transparent shadow-sm px-4 py-3 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500"
          onClick={onRemove}
        >
          <ArchiveBoxXMarkIcon className="h-5 w-5" aria-hidden="true" />
          {t("Remove")}
        </button>
        <button
          type="button"
          className="inline-flex flex-1 items-center justify-center gap-x-2 rounded-md border border-transparent shadow-sm px-4 py-3 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500"
          onClick={onDamage}
        >
          <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
          {t("Damage")}
        </button>
      </div>

      <button
        type="button"
        className="mt-4 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500 sm:text-sm"
        onClick={() => setOpen(false)}
      >
        {t("Cancel")}
      </button>
    </Modal>
  );
}
